
import { AutoPlay } from '@egjs/flicking-plugins';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'AppCarousel',
  props: {
    cols: {
      type: Number,
      default: null
    },
    autoPlay: {
      type: Boolean,
      default: false
    },
    circular: {
      type: Boolean,
      default: true
    },
    panelsPerView: {
      type: Number,
      default: null
    },
    delay: {
      type: Number,
      default: 3
    },
    showPagination: {
      type: Boolean,
      default: false
    },
    paginationOnTop: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    },
    showSideArrow: {
      type: Boolean,
      default: false
    },
    paginateByPanel: {
      type: Boolean,
      default: true
    },
    minHeight: {
      type: Number,
      default: 0
    },
    minCarouselWidth: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      curIdx: 0,
      rightEdgePosition: 0,
      currentRightEdgePosition: 0
    };
  },
  computed: {
    ...mapGetters('widget', ['device']),
    ...mapState('widget', ['widget']),
    defaultOptions() {
      return {
        align: 'prev',
        circular: this.circular && this.hasEnoughPanels,
        bound: !(this.circular && this.hasEnoughPanels),
        threshold: 0,
        disableOnInit: this.showSideArrow,
        useResizeObserver: false,
        ...this.optionsWithCols
      };
    },
    optionsWithCols() {
      const panelsPerView =
        this.panelsPerView ||
        (this.cols
          ? this.device === 'pc'
            ? this.cols
            : this.cols + 0.03
          : -1);

      if (panelsPerView > 0) {
        const moveType = ['strict', { count: Math.floor(panelsPerView) }];
        return { panelsPerView, moveType };
      }

      return { panelsPerView };
    },
    flickingPlugins() {
      return this.autoPlay
        ? [
            new AutoPlay({
              duration: this.delay * 1000,
              direction: 'NEXT',
              stopOnHover: true
            })
          ]
        : [];
    },
    panelsPerPage() {
      return this.cols || 1;
    },
    hasEnoughPanels() {
      return this.totalCount > this.optionsWithCols.panelsPerView;
    },
    curPage() {
      return Math.ceil((this.curIdx + 1) / this.panelsPerPage);
    },
    totalPage() {
      return Math.ceil(this.totalCount / this.panelsPerPage);
    },
    previousPageFirstIdx() {
      const previousPage =
        !this.circular && this.curPage === 1
          ? 1
          : this.curPage - 1 || this.totalPage;
      return (previousPage - 1) * this.panelsPerPage;
    },
    nextPageFirstIdx() {
      const nextPage =
        !this.circular && this.curPage === this.totalPage
          ? this.totalPage
          : (this.curPage % this.totalPage) + 1;
      return (nextPage - 1) * this.panelsPerPage;
    },
    pageIndicator() {
      return this.paginateByPanel
        ? `${this.curIdx + 1}/${this.totalCount}`
        : `${this.curPage}/${this.totalPage}`;
    },
    styleProps() {
      return {
        '--arrow-background-color':
          this.widget.background_color === 'transparent'
            ? ''
            : this.widget.background_color
      };
    },
    showLeftArrow() {
      return this.showSideArrow && !this.isAtLeftEdge;
    },
    showRightArrow() {
      return this.showSideArrow && !this.isAtRightEdge;
    },
    isAtLeftEdge() {
      return this.curIdx === 0;
    },
    isAtRightEdge() {
      return this.currentRightEdgePosition >= this.rightEdgePosition;
    }
  },
  mounted() {
    this.updateFlickingEdgePositions();
  },
  methods: {
    onClickPrev() {
      this.moveInCarousel('PREV');
    },
    onClickNext() {
      this.moveInCarousel('NEXT');
    },
    moveInCarousel(direction) {
      if (this.paginateByPanel) {
        if (this.$refs.flicking.animating) return;

        direction === 'PREV'
          ? this.$refs.flicking.prev().catch(() => {})
          : this.$refs.flicking.next().catch(() => {});
      } else {
        const moveParams = [
          direction === 'PREV'
            ? this.previousPageFirstIdx
            : this.nextPageFirstIdx,
          400,
          direction
        ];

        if (this.$refs.flicking.animating) {
          this.$refs.flicking.updateAnimation(...moveParams);
        } else {
          this.$refs.flicking.moveTo(...moveParams);
        }
      }
    },
    changed(e) {
      this.curIdx = e.index;
      this.updateFlickingEdgePositions();
    },
    updateFlickingEdgePositions() {
      if (!this.showSideArrow || !this.$refs.flicking) return;

      const { camera, panels } = this.$refs.flicking;

      if (!panels || panels.length === 0) return;
      this.rightEdgePosition = panels[panels.length - 1].range.max;
      this.currentRightEdgePosition = camera.visibleRange.max;
    }
  }
};
